/**
 * The API this page talks to.
 *
 * Same-origin relative paths, always. Behind CloudFront the /api behaviour
 * routes to the Lambda function and the default behaviour serves this bundle
 * from S3, so they share an origin; in development the Vite proxy reproduces
 * that. The consequence worth having is that this page needs no CORS, no
 * configured API base URL, and no build-time environment variable.
 *
 * `credentials: "same-origin"` rather than "include" for the same reason: the
 * cookies are on the parent domain and the request is same-origin, so they are
 * sent without asking for the looser mode.
 */

export type AuthenticatedUser = {
  id: string;
  email: string;
  name: string | null;
  roles: string[];
  apps: string[];
};

export type SessionState =
  | { authenticated: true; user: AuthenticatedUser; expiresAt: string }
  | { authenticated: false; loginUrl: string };

/**
 * An error carrying the API's own code, so the form can decide what to say
 * without matching on prose. The codes are the ones in
 * apps/api/src/app/cognito.py.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function readError(response: Response): Promise<ApiError> {
  // A 502 from CloudFront or a Lambda that failed before Hono ran answers HTML,
  // not JSON. Parsing has to be allowed to fail without replacing the real
  // status with a parser error.
  let code = "unexpected_error";
  let message = "Something went wrong. Try again.";

  try {
    const body = (await response.json()) as { error?: string; message?: string };
    if (body.error) code = body.error;
    if (body.message) message = body.message;
  } catch {
    // Keep the defaults.
  }

  return new ApiError(code, message, response.status);
}

/**
 * Who is signed in, if anybody.
 *
 * A 401 is an answer rather than a failure: it is what "nobody is signed in"
 * looks like, and the caller renders a form for it. Only a network fault or a
 * 5xx throws.
 */
export async function fetchSession(): Promise<SessionState> {
  const response = await fetch("/api/auth/session", {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });

  if (response.status === 401) {
    return (await response.json()) as SessionState;
  }

  if (!response.ok) throw await readError(response);

  return (await response.json()) as SessionState;
}

export type LoginInput = {
  email: string;
  password: string;
  redirectUri: string | null;
};

export type SignedInResult = {
  user: AuthenticatedUser;
  /** Validated by the API against its allowlist, never the raw query value. */
  redirectTo: string;
};

/**
 * Cognito's first-sign-in challenge. Administrators create accounts with
 * temporary passwords, so the first successful sign-in answers this instead of
 * a session: the credentials were right, and a new password finishes the job.
 * `session` is Cognito's opaque challenge state, round-tripped untouched.
 */
export type NewPasswordChallenge = {
  challenge: "new_password_required";
  session: string;
  email: string;
};

export type LoginOutcome = ({ challenge?: undefined } & SignedInResult) | NewPasswordChallenge;

export async function login(input: LoginInput): Promise<LoginOutcome> {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      email: input.email,
      password: input.password,
      redirect_uri: input.redirectUri ?? undefined,
    }),
  });

  // 409 is the challenge above: not a refusal, not yet a session.
  if (response.status === 409) {
    const body = (await response.json()) as { session: string; email: string };
    return { challenge: "new_password_required", session: body.session, email: body.email };
  }

  if (!response.ok) throw await readError(response);

  return (await response.json()) as SignedInResult;
}

export type NewPasswordInput = {
  email: string;
  newPassword: string;
  session: string;
  redirectUri: string | null;
};

export async function completeNewPassword(input: NewPasswordInput): Promise<SignedInResult> {
  const response = await fetch("/api/auth/new-password", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      email: input.email,
      new_password: input.newPassword,
      session: input.session,
      redirect_uri: input.redirectUri ?? undefined,
    }),
  });

  if (!response.ok) throw await readError(response);

  return (await response.json()) as SignedInResult;
}

export async function logout(): Promise<void> {
  const response = await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });

  if (!response.ok) throw await readError(response);
}

/**
 * The admin API (apps/api/src/app/routes/admin.py). Every call here needs a
 * session carrying the `admin` group; a session without it gets a 403 an
 * admin-dashboard caller turns into readError like any other refusal.
 */

export type Project = {
  slug: string;
  name: string;
};

export async function fetchProjects(): Promise<Project[]> {
  const response = await fetch("/api/admin/projects", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: Project[] };
  return body.projects;
}

export async function createProject(input: Project): Promise<Project> {
  const response = await fetch("/api/admin/projects", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as Project;
}

export async function deleteProject(slug: string): Promise<void> {
  const response = await fetch(`/api/admin/projects/${encodeURIComponent(slug)}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
}

/** The standard group set (docs/ARCHITECTURE.md). admin and superuser reach
 * every project; user and guests reach nothing until granted. */
export const STANDARD_GROUPS = ["admin", "superuser", "user", "guests"] as const;
export type StandardGroup = (typeof STANDARD_GROUPS)[number];

export type AdminUserSummary = {
  userId: string;
  email: string;
  name: string | null;
  enabled: boolean;
  status: string;
  groups: string[];
};

export async function fetchUsers(): Promise<AdminUserSummary[]> {
  const response = await fetch("/api/admin/users", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { users: AdminUserSummary[] };
  return body.users;
}

export async function createAdminUser(input: {
  email: string;
  name: string | null;
  groups: string[];
}): Promise<AdminUserSummary> {
  const response = await fetch("/api/admin/users", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AdminUserSummary;
}

export async function setUserGroups(userId: string, groups: string[]): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/groups`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ groups }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { groups: string[] };
  return body.groups;
}

export async function fetchUserGrants(userId: string): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/grants`, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: string[] };
  return body.projects;
}

export async function setUserGrants(userId: string, projects: string[]): Promise<string[]> {
  const response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/grants`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ projects }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as { projects: string[] };
  return body.projects;
}
